import { z } from 'zod'

// Wire contract for the Glow Intelligence chat stream. The limits mirror the backend endpoint,
// so a request that validates here is never rejected structurally by the server.
export const MAX_TURNS = 20
// Roughly 8k tokens: a memory guardrail, not a model limit; the frontend drops the
// oldest turns beyond it and the server rejects requests that still exceed it
export const MAX_CONVERSATION_CHARACTERS = 32_000
export const MAX_FEEDBACK_COMMENT_CHARACTERS = 2_000

// The service registers these routes and the frontend calls them; the base
// must match the ingress prefix for glow-intelligence in glow-deploy
export const AI_BASE_PATH = '/glow-intelligence/api'
export const AI_CHAT_STREAM_PATH = `${AI_BASE_PATH}/chat/stream`
export const AI_CONVERSATIONS_PATH = `${AI_BASE_PATH}/conversations`
export const AI_FEEDBACK_PATH = `${AI_BASE_PATH}/feedback`
export const AI_KNOWLEDGE_SYNC_PATH = `${AI_BASE_PATH}/knowledge/sync`

export const chatRoleSchema = z.enum(['user', 'assistant'])

// The models a client may ask for; which of them are actually served is deployment
// config, so the server answers 400 UNKNOWN_MODEL for a model it has no deployment for
export const CHAT_MODELS = ['gpt-4.1', 'gpt-5.4-nano', 'gpt-5.6-luna'] as const

export const chatModelSchema = z.enum(CHAT_MODELS)

export const chatTurnSchema = z.object({
  role: chatRoleSchema,
  content: z.string().trim().min(1)
})

export const ANSWER_LANGUAGES = ['en', 'nb', 'sv', 'da', 'fi', 'fr'] as const
export type AnswerLanguage = (typeof ANSWER_LANGUAGES)[number]

export const aiChatContextSchema = z.object({
  pathname: z.string().optional(),
  // The language the user chose for the chat; optional only for clients deployed before the choice existed
  answerLanguage: z.enum(ANSWER_LANGUAGES).optional()
})

export const aiChatRequestSchema = z
  .object({
    messages: z.array(chatTurnSchema).min(1).max(MAX_TURNS),
    context: aiChatContextSchema.optional(),
    // The server's configured default answers when no model is asked for
    model: chatModelSchema.optional(),
    // Returned by the first done event; echoing it threads later exchanges into one conversation
    conversationId: z.uuid().optional()
  })
  .refine((request) => request.messages.at(-1)?.role === 'user', {
    message: 'the last message must come from the user'
  })
  .refine(
    (request) => request.messages.reduce((sum, turn) => sum + turn.content.length, 0) <= MAX_CONVERSATION_CHARACTERS,
    { message: `a conversation may hold at most ${MAX_CONVERSATION_CHARACTERS} characters` }
  )

/*
The response stream follows the AI SDK UI message stream protocol. The one custom piece
is the exchange data part (type 'data-exchange') the server appends once the exchange is
persisted; exchangeId identifies the answer for feedback and conversationId is echoed in
the next request to thread the conversation.
*/
export const EXCHANGE_DATA_PART = 'data-exchange' as const

export const exchangeDataSchema = z.object({
  conversationId: z.uuid(),
  exchangeId: z.uuid()
})

/*
Tools the assistant may call while answering. Tool activity streams as UI message
parts typed 'tool-<name>', so the names and schemas live here for the drawer to
render the same typed inputs and outputs the service executes.
*/
export const GLOSSARY_TOOL = 'glossary' as const
export const GLOSSARY_TOOL_PART = `tool-${GLOSSARY_TOOL}` as const

export const glossaryInputSchema = z.object({
  term: z.string().describe('The term, abbreviation or product name to look up')
})

export const glossaryOutputSchema = z.object({
  // null when the glossary has no entry, so the model says so instead of guessing
  definition: z.string().nullable()
})

// Written before the first token, so the client can show them while the answer streams. Emitted
// from retrieval rather than composed by the model, which eventually invents a plausible url
export const SOURCES_DATA_PART = 'data-sources' as const

export const knowledgeSourceSchema = z.object({
  title: z.string(),
  // https only, since this is rendered as an href and plain z.url() accepts javascript: and data:.
  // Declares the contract rather than enforcing it - nothing parses this schema at runtime, so the
  // check that matters is on the article url at ingestion. Absent for a reader who may not open the
  // page (product docs, for non-super-admins)
  url: z.url({ protocol: /^https$/ }).optional(),
  // The source system's own timestamp, so a reader can judge staleness themselves
  updatedAt: z.iso.datetime(),
  // Which knowledge source the citation came from ('glow-code'); the server
  // decides by it what each audience may open, hiding anything unlabelled from non-super-admins
  source: z.string().optional(),
  // The app the document describes ('internal-frontend', 'customer-frontend', 'tracking'); absent
  // on exchanges persisted before apps were labelled, which means Glow
  app: z.string().optional()
})

export const sourcesDataSchema = z.object({
  sources: z.array(knowledgeSourceSchema)
})

// The label keys the answer's excerpts carried, written before the first token of every answer: the client
// translates a label only under one of these, so a key the model invented never shows
export const LABEL_KEYS_DATA_PART = 'data-labelKeys' as const

// **Label** (`key`), as the documents quote on-screen labels and answers repeat them; group 1 the label, which
// may hold an escaped \*, group 2 the key. One pattern for the server that collects keys and the client that checks them
export const labelPairs = (): RegExp => /\*\*((?:\\\*|[^*\n])+)\*\* ?\(`([^`\n]*)`\)/g

export const labelKeysDataSchema = z.object({
  keys: z.array(z.string())
})

export const feedbackRequestSchema = z.object({
  exchangeId: z.uuid(),
  helpful: z.boolean(),
  comment: z.string().trim().min(1).max(MAX_FEEDBACK_COMMENT_CHARACTERS).optional()
})

export const MAX_HISTORY_CONVERSATIONS = 20

// The first question stands in as the title; the client truncates for display
export const conversationSummarySchema = z.object({
  conversationId: z.uuid(),
  title: z.string(),
  lastActivityAt: z.iso.datetime()
})

export const conversationListSchema = z.object({
  conversations: z.array(conversationSummarySchema)
})

// helpful is the requesting user's rating of the answer, so reopening a conversation
// shows the thumbs already given
export const historyExchangeSchema = z.object({
  exchangeId: z.uuid(),
  question: z.string(),
  answer: z.string(),
  helpful: z.boolean().nullable(),
  // Empty for answers stored before this existed, and for answers given without retrieval
  sources: z.array(knowledgeSourceSchema),
  // Absent for answers stored without their excerpts, which the client localizes as before
  labelKeys: z.array(z.string()).optional(),
  createdAt: z.iso.datetime()
})

export const conversationHistorySchema = z.object({
  conversationId: z.uuid(),
  exchanges: z.array(historyExchangeSchema)
})

export type ChatRole = z.infer<typeof chatRoleSchema>
export type ChatModel = z.infer<typeof chatModelSchema>
export type ChatTurn = z.infer<typeof chatTurnSchema>
export type AiChatContext = z.infer<typeof aiChatContextSchema>
export type AiChatRequest = z.infer<typeof aiChatRequestSchema>
export type ExchangeData = z.infer<typeof exchangeDataSchema>
export type GlossaryInput = z.infer<typeof glossaryInputSchema>
export type GlossaryOutput = z.infer<typeof glossaryOutputSchema>
export type KnowledgeSource = z.infer<typeof knowledgeSourceSchema>
export type SourcesData = z.infer<typeof sourcesDataSchema>
export type LabelKeysData = z.infer<typeof labelKeysDataSchema>
export type FeedbackRequest = z.infer<typeof feedbackRequestSchema>
export type ConversationSummary = z.infer<typeof conversationSummarySchema>
export type ConversationList = z.infer<typeof conversationListSchema>
export type HistoryExchange = z.infer<typeof historyExchangeSchema>
export type ConversationHistory = z.infer<typeof conversationHistorySchema>
